/**
 * Shared helpers for the Select combobox.
 */

/**
 * @param {any} option
 * @returns {string}
 */
export function getOptionLabel(option) {
  if (!option) return "";

  if (typeof option.label === "string" || typeof option.label === "number") {
    return String(option.label);
  }

  return (
    option.fullName ||
    option.name ||
    option.title ||
    option.searchLabel ||
    option.partNumber ||
    ""
  );
}

/**
 * @param {any} option
 * @returns {boolean}
 */
export function isNestedCategory(option) {
  return Array.isArray(option?.options) && option.options.length > 0;
}

/**
 * Flatten nested category options into leaf options.
 *
 * @param {Array<any>} options
 * @returns {Array<any>}
 */
export function flattenOptions(options = []) {
  return options.flatMap((option) => {
    if (isNestedCategory(option)) {
      return flattenOptions(option.options);
    }
    return [option];
  });
}

/**
 * @param {any} option
 * @param {string[]} [searchKeys]
 * @returns {string}
 */
export function getOptionSearchText(option, searchKeys = []) {
  const chunks = [getOptionLabel(option)];

  searchKeys.forEach((key) => {
    const value = option?.[key];
    if (value != null && value !== "") {
      chunks.push(String(value));
    }
  });

  if (option?.value && typeof option.value === "object") {
    searchKeys.forEach((key) => {
      const value = option.value?.[key];
      if (value != null && value !== "") {
        chunks.push(String(value));
      }
    });
  } else if (option?.value != null && typeof option.value !== "object") {
    chunks.push(String(option.value));
  }

  return chunks.join(" ").toLowerCase();
}

/**
 * @param {Array<any>} options
 * @param {string} query
 * @param {string[]} [searchKeys]
 * @returns {Array<any>}
 */
export function filterOptions(options = [], query = "", searchKeys = []) {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;

  return options.filter((option) => {
    if (isNestedCategory(option)) {
      return getOptionSearchText(option, searchKeys).includes(needle);
    }

    return getOptionSearchText(option, searchKeys).includes(needle);
  });
}

/**
 * Resolve the selected option from a caller-provided value.
 *
 * @param {Array<any>} options
 * @param {any} value
 * @param {{ displayValueFrom?: "label"|"value" }} [config]
 * @returns {any|null}
 */
export function findSelectedOption(
  options = [],
  value,
  { displayValueFrom = "label" } = {},
) {
  if (value == null || value === "") return null;

  const flatOptions = flattenOptions(options);

  if (typeof value === "object") {
    const valueId =
      value._id || value.id || value.value?._id || value.value?.id || null;

    if (valueId) {
      const byId = flatOptions.find((option) => {
        const optionValue = option?.value;
        const optionId =
          optionValue?._id ||
          optionValue?.id ||
          option?._id ||
          option?.id ||
          null;
        return optionId && String(optionId) === String(valueId);
      });
      if (byId) return byId;
    }

    if (value.label && value.value) {
      return value;
    }
  }

  if (displayValueFrom === "value") {
    const byValue = flatOptions.find(
      (option) =>
        option.value === value || String(option.value) === String(value),
    );
    if (byValue) return byValue;
  }

  const valueString = String(value);
  const byLabel = flatOptions.find(
    (option) => getOptionLabel(option) === valueString,
  );
  if (byLabel) return byLabel;

  return (
    flatOptions.find(
      (option) =>
        option.value === value || String(option.value) === valueString,
    ) || null
  );
}

/**
 * @param {any} input
 * @returns {any}
 */
export function normalizeSelection(input) {
  if (!input || input.value === "EMPTY_VALUE") {
    return null;
  }

  if (
    input &&
    typeof input === "object" &&
    ("label" in input || "body" in input) &&
    !isNestedCategory(input)
  ) {
    return input;
  }

  if (input && typeof input === "object") {
    return {
      ...input,
      value: input.value ?? input,
      label: getOptionLabel({ ...input, value: input.value ?? input }),
    };
  }

  return {
    value: input,
    label: String(input ?? ""),
  };
}

/**
 * @param {any} option
 * @param {Array<any>} categoryStack
 * @returns {string}
 */
export function buildFullValue(option, categoryStack = []) {
  const label = getOptionLabel(option);
  if (!categoryStack.length) return label;

  const prefix = categoryStack.map((item) => getOptionLabel(item)).join(" - ");
  return `${prefix} - ${label}`;
}

/**
 * @param {any} option
 * @param {string} [displayValueFrom]
 * @returns {string}
 */
export function getDisplayValueFromOption(option, displayValueFrom = "label") {
  if (!option) return "";

  if (displayValueFrom === "value" && option.value != null) {
    return String(option.value);
  }

  return getOptionLabel(option);
}
